import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Countdown } from "./Countdown";
import { EASE } from "./motion";
import { DRIVER } from "@/lib/content";
import { nextRace } from "@/lib/race";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/context/LangContext";

const HeroLine = ({ children, show, delay, className }) => (
  <span className="block overflow-hidden pb-[0.02em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={show ? { y: "0%" } : { y: "105%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}>
      {children}
    </motion.span>
  </span>
);

const FadeIn = ({ show, delay, children, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 16 }} animate={show ? { opacity: 1, y: 0 } : {}}
    transition={{ duration: 0.8, ease: EASE, delay }}>
    {children}
  </motion.div>
);

export const Hero = ({ ready, races }) => {
  const ref = useRef(null);
  const { t, lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const nameY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 70, damping: 18 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 70, damping: 18 });
  const shiftX = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMove} data-testid="hero-section"
      className="relative flex min-h-[100svh] flex-col overflow-hidden bg-ink pt-20 md:pt-0">
      <motion.div aria-hidden style={{ rotateX: rotX, rotateY: rotY, transformPerspective: 1100 }}
        className="pointer-events-none absolute -right-[6vw] top-[6vh] select-none font-display text-[70vw] font-black leading-[0.8] text-outline md:text-[46vw]">
        77
      </motion.div>

      <div className="relative mx-5 h-[50svh] md:absolute md:right-[6vw] md:top-[11vh] md:mx-0 md:h-[78vh] md:w-[36vw] lg:w-[32vw]">
        <div className="hero-clip relative h-full w-full overflow-hidden">
          <motion.img src="/images/lucas.jpg" alt={t.hero.img_alt} style={{ y: imgY, x: shiftX }}
            className="absolute inset-0 h-[120%] w-full scale-110 object-cover object-[50%_20%]" data-testid="hero-image" />
          <div className="spotlight absolute inset-0" />
          <motion.div className="absolute inset-0 origin-right bg-trident" initial={{ scaleX: 1 }}
            animate={ready ? { scaleX: 0 } : { scaleX: 1 }} transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1], delay: 0.05 }} />
        </div>
        <FadeIn show={ready} delay={0.9} className="absolute -bottom-3 right-3 md:-left-2 md:bottom-auto md:right-auto md:top-6">
          <span className="label-mono bg-trident px-3 py-1.5 font-bold text-white">{DRIVER.team} · #{DRIVER.number}</span>
        </FadeIn>
      </div>

      <motion.div style={{ y: nameY }} className="relative z-10 mt-8 flex flex-1 flex-col justify-end px-5 pb-8 md:mt-0 md:px-10 md:pb-10">
        <FadeIn show={ready} delay={0.35} className="label-mono mb-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-zinc-300">
          <span className="flex items-center gap-2"><span className="flex h-3 w-5 overflow-hidden"><i className="flex-1 bg-[#009246]" /><i className="flex-1 bg-white" /><i className="flex-1 bg-flag" /></span>{lang === "en" ? "Italy" : DRIVER.nationality}</span>
          <span>{lang === "en" ? "Italian F4 Championship" : DRIVER.championship}</span>
          <span className="text-volt">{t.hero.season} {DRIVER.season}</span>
        </FadeIn>
        <h1 className="font-display font-black uppercase leading-[0.8] tracking-[-0.01em]" data-testid="hero-title">
          <HeroLine show={ready} delay={0.1} className="text-[16vw] text-white md:text-[11.5vw]">{DRIVER.firstName}</HeroLine>
          <HeroLine show={ready} delay={0.22} className="text-[16vw] text-volt md:text-[11.5vw]">{DRIVER.lastName}</HeroLine>
        </h1>
        <FadeIn show={ready} delay={0.6} className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-wrap gap-3">
            <button onClick={() => scrollToId("bio")} data-testid="hero-cta-bio"
              className="group flex items-center gap-2 rounded-full bg-white px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,color,transform] duration-300 hover:scale-[1.03] hover:bg-trident hover:text-white">
              {t.hero.cta_bio} <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button onClick={() => scrollToId("calendario")} data-testid="hero-cta-calendar"
              className="rounded-full border border-white/25 px-6 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-volt hover:text-volt">
              {t.hero.cta_cal} {DRIVER.season}
            </button>
          </div>
          <Countdown race={nextRace(races)} />
        </FadeIn>
        <FadeIn show={ready} delay={1} className="label-mono mt-8 hidden items-center gap-3 text-white md:flex">
          <ArrowDown className="h-4 w-4 animate-bounce text-volt" /> {t.hero.scroll}
        </FadeIn>
      </motion.div>
    </section>
  );
};
